import { AfterViewInit, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  Parking,
  ParkingListRequest,
  ParkingListResponse,
} from '@core/models/parking-list.model';
import { ParkingService } from '../services/parking.service';
import {
  MSG_DELETE_PARKING,
  MSG_DELETE_PARKING_CONFIRMATION,
  OCCUPANCY_TOOLTIP_MESSAGE
} from '@shared/constant/shared.constant';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ToastService } from '@shared/services/toast.service';
import { MenuService } from '@core/services/menu.service';
import { CommonMenuService } from '@shared/services/common-util.service';
import { autoUnsubWith } from '@shared/services/utility.abstract';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';
import { CommonFunctions } from '@shared/common-functions';

@Component({
  selector: 'app-parking-list',
  templateUrl: './parking-list.component.html',
  styleUrls: ['./parking-list.component.scss'],
})
export class ParkingListComponent implements OnInit, AfterViewInit {
  readonly destroyRef = inject(DestroyRef);
  searchQuery = '';
  parkingList: Parking[] = [];
  first = 0;
  rows = 10;
  totalItems!: number;
  itemsPerPage = 10;
  currentPage = 1;
  emptyParkingListMsg = '';
  roleName: string;
  activeItem: any;
  items: MenuItem[] | undefined;
  createParkingCheck: boolean = false;
  checked: boolean = false;
  readonly occupancyTooltipMsg = OCCUPANCY_TOOLTIP_MESSAGE

  constructor(
    private readonly router: Router,
    private readonly parkingService: ParkingService,
    private readonly confirmationService: ConfirmationService,   
    private readonly toastService: ToastService,
    private readonly menuService: MenuService,
    private readonly commonMenuService: CommonMenuService,
    private readonly encryptStorage: EncryptionStorageService
  ) { }

  ngOnInit(): void {
    
    this.getParkingLotsByManagerId('pageReload');
    this.roleName = localStorage['roleName'];
    this.resetPagination();
    this.createParkingCheck = this.menuService.shortNameExists(
      JSON.parse(this.encryptStorage.getItem('menuData')),
      72
    );
  }

  ngAfterViewInit(): void {
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
  }

  createParking() {
    localStorage.removeItem('navigationFromView');
    this.router.navigateByUrl('/manager/parking/create');
  }

  getParkingLotsByManagerId(changeAction) {
    if (changeAction === 'search') {
      this.currentPage = 1;
      this.first = this.currentPage;
    }
    
    const parkingListRequest: ParkingListRequest = {
      search: this.searchQuery.toLowerCase(),
      pageNumber: this.currentPage,
      pageSize: this.itemsPerPage,
    };
    this.parkingService
      .getParkingLotsByManagerId(parkingListRequest)
      .pipe(autoUnsubWith(this.destroyRef))
      .subscribe({
        next: (response: ParkingListResponse) => {
          this.parkingList = response.parkingList;
          this.totalItems = response.totalRecords;
          
        },
        error: () => {
          
          this.parkingList = [];
          this.totalItems = 0;
          this.currentPage = 1;
          this.emptyParkingListMsg =
            'There are no parkings yet, create one to get started!';
        },
      });
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.currentPage = event.page + 1;
    this.getParkingLotsByManagerId('pageChange');
    CommonFunctions.scrollTopRow('parking-list');
  }

  showCount(vehicleCount: number): boolean {
    return vehicleCount > 3;
  }

  viewParkingDetailsById(id: string) {
    localStorage.removeItem('navigationFromView');
    this.router.navigate(['/manager/parking/view', id]);
  }

  getVehicleAllowed(vehicleList: any) {
    if (vehicleList) {
      const size = 3;
      return vehicleList.slice(0, size);
    } else {
      return [];
    }
  }

  editParkingNavigation(parkingId: string): void {
    localStorage.removeItem('navigationFromView');
    this.router.navigate(['manager/parking/edit', parkingId]);
  }

  clearSearch(_event: any): void {
    if (_event.trim() == '') {
      this.searchQuery = '';
      this.resetPagination();
      this.getParkingLotsByManagerId('clearSearch');
    }
  }

  resetPagination(): void {
    this.currentPage = 1;
    this.first = 0;
  }

  updateParkingLotStatus(parkingLodId: string) {
    
    const payload = {
      parkingLotId: parkingLodId,
    };
    this.parkingService.updateParkingLotStatus(payload)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: (response: any) => {
        this.toastService.showToastMessage(
          'success',
          'Success',
          response.message
        );
        
        this.getParkingLotsByManagerId('activeInactiveParkingLot');
      },
      error: (err: Error) => {
        this.toastService.showToastMessage('error', 'Error', err.message);
        
      },
    });
  }

  deleteComingSoonParkingById(id) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: MSG_DELETE_PARKING_CONFIRMATION,
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.parkingService.deleteComingsoonParking(id)
          .pipe(
            autoUnsubWith(this.destroyRef)
          ).subscribe(
            (_res: any) => {
              this.getParkingLotsByManagerId('deleteComingSoonParking');
              this.toastService.showToastMessage(
                'success',
                'Success',
                MSG_DELETE_PARKING
              );
          },
        );
      },
    });
  }

  showData(data) {
    const showIcon = 'pi pi-eye';
    const editIcon = 'pi pi-file-edit';

    const actionMap = {
      viewParking: () => {
        this.router.navigate(['manager/parking/view', this.activeItem.id]);
      },
      edit: () => {
        this.editAction(); // Assuming editAction is already defined
      },
      openGate: () => {
        this.openGate(data.id);
      },
    };

    const config = [
      {
        label: '',
        icon: '',
      },
     
      {
        label: 'View Parking',
        icon: showIcon,
        visible: this.menuService.shortNameExists(
          JSON.parse(this.encryptStorage.getItem('menuData')),
          37
        ),
        command: actionMap.viewParking,
      },
      {
        label: 'Edit',
        icon: editIcon,
        visible: this.menuService.shortNameExists(
          JSON.parse(this.encryptStorage.getItem('menuData')),
          71
        ) && !(data.isDeleted),
        command: actionMap.edit,
      },
      {
        label: 'Open Gate',
        icon: 'common-icon gate-icon',
        visible : data.isGateIntegration,
        command: actionMap.openGate,
      },
    
    ];

    this.items = this.commonMenuService.commonShowData(config);
  }

  editAction() {
    localStorage.removeItem('navigationFromView');
    this.router.navigate(['manager/parking/edit', this.activeItem.id], {});
  }

  openGate(id: string) {
    this.parkingService.openGateById(id)
      .pipe(
        autoUnsubWith(this.destroyRef)
      )
      .subscribe({
        next: (response: any) => {
          this.toastService.showToastMessage(
            'success',
            'Success',
          response.message
        );
        
      },
      error: (err: any) => {
        this.toastService.showToastMessage('error', 'Error', err.error.message);
        
      }
    });
  }

  get showActions(): boolean {
    return (
      this.menuService.shortNameExists(
        JSON.parse(this.encryptStorage.getItem('menuData')),
        71
      ) ||
      this.menuService.shortNameExists(
        JSON.parse(this.encryptStorage.getItem('menuData')),
        37
      )
    );
  }
}
